import React, { lazy, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { Redirect, useLocation } from 'react-router-dom';
import { last } from 'lodash';

import Page, { Content } from '../../library/page/page';
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import DefaultLayout from '../../layouts/default-layout/default-layout';

import { GET_CMS_PAGE } from './graphql/cms-page-queries';

import {
  useDataLayer,
  useMyAccount,
  useQuery,
  useScrollTo
} from '../../shared/hooks/hooks';

import { parseCmsContentAndMapComponents } from '../../shared/utils/cms/cms';
import { getCmsPageId, metaTags } from '../../shared/utils/page/page';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import * as DATA_LAYER from '../../shared/constants/data-layer';
import {
  PAGE_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE
} from '../../shared/constants/data-layer';

import styles from './cms-page.scss';

const BrandsByCategoryList = lazy(() =>
  import(
    /* webpackChunkName: "brands-by-category-list" */
    '../../components/brands-by-category-list/brands-by-category-list'
  )
);

const CECHours = lazy(() =>
  import(
    /* webpackChunkName: "cec-hours" */
    '../../components/cec-hours/cec-hours'
  )
);

const CMSAuthModal = lazy(() =>
  import(
    /* webpackChunkName: "cms-auth-modal" */
    './components/cms-auth-modal/cms-auth-modal'
  )
);

const FleetFreeServicesCalculator = lazy(() =>
  import(
    /* webpackChunkName: "fleet-free-services-calculator" */
    '../../components/fleet-free-services-calculator/fleet-free-services-calculator'
  )
);

const HowToReadDataReportSection = lazy(() =>
  import(
    /* webpackChunkName: "how-to-read-data-report-section" */
    '../../components/how-to-read-data-report-section/how-to-read-data-report-section'
  )
);

const HowToReadLifespanCostWarrantyInfoSection = lazy(() =>
  import(
    /* webpackChunkName: "how-to-read-lifespan-cost-warranty-info-section" */
    '../../components/how-to-read-lifespan-cost-warranty-info-section/how-to-read-lifespan-cost-warranty-info-section'
  )
);

const ProductsByBrandList = lazy(() =>
  import(
    /* webpackChunkName: "products-by-brand-list" */
    '../../components/products-by-brand-list/products-by-brand-list'
  )
);

const ScheduleAppointmentButton = lazy(() =>
  import(
    /* webpackChunkName: "schedule-appointment-button" */
    '../../components/schedule-appointment-button/schedule-appointment-button'
  )
);

const StoreServiceList = lazy(() =>
  import(
    /* webpackChunkName: "store-service-list" */
    '../../components/store-service-list/store-service-list'
  )
);

const TirePressureCostCalculator = lazy(() =>
  import(
    /* webpackChunkName: "tire-pressure-cost-calculator" */
    '../../components/tire-pressure-cost-calculator/tire-pressure-cost-calculator'
  )
);

const TireSizeCalculator = lazy(() =>
  import(
    /* webpackChunkName: "tire-size-calculator" */
    '../../components/tire-size-calculator/tire-size-calculator'
  )
);

const TreadwellFitment = lazy(() =>
  import(
    /* webpackChunkName: "treadwell-fitment" */
    '../../components/treadwell-fitment/treadwell-fitment'
  )
);

const WiperCategoryTiles = lazy(() =>
  import(
    /* webpackChunkName: "wiper-category-tiles" */
    '../../components/wiper-category-tiles/wiper-category-tiles'
  )
);

export const componentMap = new Map([
  ['brands-by-category-list', BrandsByCategoryList],
  ['calculators/fleet-free-services', FleetFreeServicesCalculator],
  ['calculators/low-tire-pressure-cost', TirePressureCostCalculator],
  ['calculators/tire-size', TireSizeCalculator],
  ['cec-hours', CECHours],
  ['cms-auth-modal', CMSAuthModal],
  ['how-to-read-data-report-section', HowToReadDataReportSection],
  [
    'how-to-read-lifespan-cost-warranty-info-section',
    HowToReadLifespanCostWarrantyInfoSection
  ],
  ['products-by-brand-list', ProductsByBrandList],
  ['schedule-appointment-button', ScheduleAppointmentButton],
  ['store-service-list', StoreServiceList],
  ['treadwell-fitment', TreadwellFitment],
  ['wiper-category-tiles', WiperCategoryTiles]
]);

const pagesToHideMyAccountHeaderLinkRegExp = /\/fleet/;

function CMSPage() {
  const cmsPageId = getCmsPageId(window.location.pathname);
  const hideMyAccountHeaderLink = useMemo(
    () => pagesToHideMyAccountHeaderLinkRegExp.test(cmsPageId),
    [cmsPageId]
  );
  const { data, error, isLoading } = useQuery(GET_CMS_PAGE, {
    variables: {
      id: cmsPageId
    }
  });

  const { updateDataLayer } = useDataLayer();
  const { isFleetAccount, isLoggedIn } = useMyAccount();

  const { cms } = data || {};
  const { page: cmsPage } = cms || {};
  const {
    breadcrumbs = [],
    documentTitle = '',
    htmlContent = '',
    metaTags: metaData,
    pageAnalytics = []
  } = cmsPage || {};

  const hasBreadCrumbs = isArrayWithLength(breadcrumbs);
  const hasPageAnalytics = isArrayWithLength(pageAnalytics);
  const content = parseCmsContentAndMapComponents(htmlContent, componentMap);

  let pageBreadcrumbs = [{ name: 'home', url: '/' }];

  if (!window.isPreRendering) {
    pageBreadcrumbs = breadcrumbs;
  }

  const location = useLocation();
  const scrollTo = useScrollTo(location.hash, { offset: 120 });
  useEffect(() => {
    if (location.hash && location.hash !== '#' && content.length > 0) {
      scrollTo();
    }
  }, [content, location.hash, scrollTo]);

  useEffect(() => {
    if (hasPageAnalytics || hasBreadCrumbs) {
      const dataLayer = {};
      if (hasPageAnalytics) {
        pageAnalytics.forEach(pageAnalytic => {
          const { name, value } = pageAnalytic || {};

          if (name && value && DATA_LAYER?.[name]) {
            dataLayer[DATA_LAYER[name]] = value;
          }
        });
      } else if (hasBreadCrumbs) {
        dataLayer[PAGE_NAME] = breadcrumbs.reduce(
          (acc, b, i) =>
            acc + `${b.name}${breadcrumbs.length - 1 !== i ? ' : ' : ''}`,
          ''
        );
        dataLayer[PAGE_SUBCATEGORY] =
          breadcrumbs.length > 0 ? `${last(breadcrumbs).name}` : '';
        dataLayer[PAGE_TYPE] = 'content';
      }

      if (Object.keys(dataLayer).length !== 0) {
        setTimeout(() => {
          updateDataLayer(dataLayer);
        }, 100); // Add a delay to insure link trackActions fire before trackView's
      }
    }
  }, [
    breadcrumbs,
    hasBreadCrumbs,
    hasPageAnalytics,
    pageAnalytics,
    updateDataLayer
  ]);

  useEffect(() => {
    function getPitpassStoreCode() {
      let storeCode;
      if (process.env.__SITE_ENV__ === 'stg') {
        storeCode = '1282';
      } else if (process.env.__SITE_ENV__ === 'qa') {
        storeCode = '1127';
      } else if (process.env.__SITE_ENV__ === 'dev') {
        storeCode = '1284';
      } else {
        storeCode = '2274';
      }
      return storeCode;
    }
    const storeCode = getPitpassStoreCode();

    const isPitpassLandingPage = location.pathname === '/pitpass';
    const isFromDTAT =
      document.referrer.includes('discounttire') ||
      document.referrer.includes('americastire') ||
      location.state?.prevPage;

    if (
      isPitpassLandingPage &&
      !isFromDTAT &&
      !location.search.includes('storeCode')
    ) {
      window.location.replace(`/pitpass?storeCode=${storeCode}`);
    }
  }, [htmlContent, location]);

  const userStyleName = useMemo(
    () =>
      process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount
        ? 'fleet-user'
        : 'regular-user',
    [isFleetAccount]
  );

  if (error && !window.isPreRendering) {
    throw error;
  }

  if (
    process.env.__FF_FLEET_ACCOUNT__ === 'true' &&
    isFleetAccount &&
    location.pathname === '/promotions'
  ) {
    return <Redirect to="/fleet/promotions" />;
  }

  return (
    <DefaultLayout hideMyAccountHeaderLink={hideMyAccountHeaderLink}>
      {!window.isPreRendering && (
        <Helmet>
          <title>{documentTitle}</title>
          {metaTags(metaData || [])}
          {documentTitle === '404' && (
            <meta content="404" name="prerender-status-code" />
          )}
        </Helmet>
      )}
      <Page>
        {!window.isPreRendering && pageBreadcrumbs && (
          <Breadcrumbs crumbs={pageBreadcrumbs} />
        )}
        {!window.isPreRendering && (
          <>
            {isLoading ? (
              <LoadingSpinner className={styles['loading']} />
            ) : (
              <Content
                className={
                  isLoggedIn
                    ? `${styles['logged-in']} ${styles[userStyleName]}`
                    : styles['logged-out']
                }
              >
                {content}
              </Content>
            )}
          </>
        )}
      </Page>
    </DefaultLayout>
  );
}

export default hot(CMSPage);
